import { Box, IconButton, Paragraph } from '@zillow/constellation-10';
import { AvatarGroup, Bone, IconButtonTooltip } from '@zg-rentals/satellite-components';
import { IconCloseFilled, IconExternalFilled } from '@zillow/constellation-icons';
import { useMemo } from 'react';
import getActiveParticipantNames from '../../../../utils/getActiveParticipantNames';
import { CLICK_ON_VIEW_IN_MESSAGES_ICON_EVENT_TYPE_ID } from '../../../../constants/clickstreamEvents/leadManagementMessageWindow';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import { useConversationMessagesQuery } from '../../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';

type RenterMessagingModalHeaderProps = {
  conversationId: string;
  clientTimeZone: string;
  listingAlias: string;
  onClose: () => void;
  onOpenInInbox: () => void;
};

export type RenterMessagingModalHeaderData = {
  activeParticipants: ConversationMessagesData['activeParticipants'];
  isPhoneLead: boolean;
  renterName: string;
};

export const selectRenterMessagingModalHeaderData = (
  data: ConversationMessagesData,
): RenterMessagingModalHeaderData => ({
  activeParticipants: data.activeParticipants,
  isPhoneLead: data.isPhoneLead,
  renterName: data.renterName ?? '',
});

export default function RenterMessagingModalHeader({
  conversationId,
  clientTimeZone,
  listingAlias,
  onClose,
  onOpenInInbox,
}: RenterMessagingModalHeaderProps) {
  // The inbox slug is positional ([listingAlias, conversationId]) — degrade to the inbox root without an alias.
  const inboxUrl = listingAlias ? `/rental-manager/inbox/${listingAlias}/${conversationId}` : '/rental-manager/inbox';
  // Read lead data from React Query cache for instant display
  const cachedLead = useCachedLeadByConversationId(conversationId);

  // Cache-only read — the modal's MessageListItems owns the getMessages fetch + polling,
  // so the full participant list arrives on the same single request.
  const { data: conversation, isPending } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectRenterMessagingModalHeaderData,
  });

  // Compute participants - prefer conversation data, fallback to cached lead
  const participants = useMemo(() => {
    if (conversation) {
      const { fullParticipantList } = getActiveParticipantNames({
        activeParticipantsInConversation: conversation.activeParticipants,
        referenceName: conversation.renterName,
      });
      return fullParticipantList;
    }
    // Fallback to cached lead data
    const cachedName = cachedLead?.renterInfo?.renterName;
    const isPhoneLead = cachedLead?.latestContact?.isPhoneLead;
    if (isPhoneLead && !cachedName) return [''];
    return cachedName ? [cachedName] : [''];
  }, [conversation, cachedLead]);

  // Compute display name - prefer conversation data, fallback to cached lead
  const displayName = useMemo(() => {
    if (conversation) {
      const { displayNames } = getActiveParticipantNames({
        activeParticipantsInConversation: conversation.activeParticipants,
        referenceName: conversation.renterName,
      });
      if (conversation.isPhoneLead && !displayNames) return '(Phone lead)';
      return displayNames || 'Renter';
    }
    // Fallback to cached lead data
    const cachedName = cachedLead?.renterInfo?.renterName;
    const isPhoneLead = cachedLead?.latestContact?.isPhoneLead;
    if (isPhoneLead && !cachedName) return '(Phone lead)';
    return cachedName || 'Renter';
  }, [conversation, cachedLead]);

  // Only show skeleton if both the lead cache and the messages cache are empty
  const isLoading = isPending && !cachedLead;

  return (
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', width: '100%' }}>
      <AvatarGroup participants={isLoading ? [''] : participants} variant="default" singleParticipantSize="sm" />
      {isLoading ? (
        <Bone type="line" css={{ flex: 1 }} />
      ) : (
        <Paragraph textStyle="body-bold" css={{ flex: 1, truncate: true }}>
          {displayName}
        </Paragraph>
      )}
      <IconButtonTooltip
        content="View in messages"
        trigger={
          <IconButton
            asChild
            data-eventid={CLICK_ON_VIEW_IN_MESSAGES_ICON_EVENT_TYPE_ID}
            title="View in messages"
            icon={<IconExternalFilled />}
            shape="circle"
            size="sm"
            emphasis="bare"
          >
            <a href={inboxUrl} target="_blank" rel="noopener noreferrer" onClick={onOpenInInbox} />
          </IconButton>
        }
      />
      <IconButtonTooltip
        content="Close"
        trigger={
          <IconButton
            title="Close"
            icon={<IconCloseFilled />}
            shape="circle"
            size="sm"
            emphasis="bare"
            onClick={onClose}
          />
        }
      />
    </Box>
  );
}
